"use client";

/**
 * Scene 2 — The Gap. THE MAIN WOW.
 *
 * Cinematic waterfall scroll-pin: AI tooling TAM ($1.0B ex-Cursor, Q1 2026)
 * → code builders shave (-75%) → landing builders shave (-25%) → uncovered
 * gap (<2%, <$30M — Foundry's slice, emerald sliver).
 *
 * Spec sources (order of authority):
 *   1. research/landing-v2/09-wireframe-v2-part-a.md §4 (this file's spec)
 *   2. research/landing-v2/07-market-research.md §4 (numbers)
 *   3. research/landing-v2/05-landing-concept-v2.md §4.3 (concept)
 *   4. src/components/niche-v3/shared/WaterfallScrollPin.tsx (motion pattern)
 *
 * Adapter decision (per task brief):
 *   We do NOT reuse niche-v3/shared/WaterfallScrollPin or write an adapter.
 *   Rationale:
 *     (a) Niche v3 shape is TAM / competitionShave / acquisitionShave / SOM,
 *         parsed from human-readable strings ("$4.2B", "-65% held...").
 *         Landing needs 4 distinct labeled bars with per-tier competitor
 *         lists rendered in the narrative column (NOT bar labels), plus a
 *         visualPercent clamp on the emerald sliver (real <2% → 12% visual).
 *     (b) Bar 2 / Bar 3 narrative copy is tier-specific ("They build the app
 *         once you know what" vs "They build the page once you know who's
 *         buying"). Hard to express in a generic adapter.
 *     (c) Motion pattern (useScroll + useTransform) IS reused — we copy the
 *         scroll-band shape but render landing-specific content inline.
 *   Net: inline implementation keeps primitive complexity low and copy
 *   authoring obvious (all strings in one file, aligned with §4.7 wireframe).
 *
 * Reduced-motion:
 *   - Sticky pin disabled (container becomes h-auto).
 *   - All 4 bars render at final width on mount.
 *   - All 4 narrative blocks render static stacked (no opacity/y binding).
 *   - AnimatedCounter → instant final value via primitive's own handling.
 *   - Why Now callout fade-in 200ms (no y-translate).
 *
 * a11y:
 *   - Entire scene is non-interactive (no tab stops).
 *   - <section role="region" aria-labelledby="scene-2-gap-title">
 *   - SceneHeader = <h2 id="scene-2-gap-title">. Hook below renders as the
 *     SceneHookHero default (<p>) — not a heading. Keeping a single h2 per
 *     scene avoids docstring-vs-DOM drift (DB-01 audit, S101).
 *   - Chart = <figure role="img" aria-labelledby="waterfall-caption"> with
 *     sr-only figcaption containing the full numeric story in prose, so
 *     screen readers get the meaning regardless of scroll position.
 *   - AnimatedCounter handles its own aria-label (final value only).
 */

import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { Tooltip } from "@base-ui/react/tooltip";
import { cn } from "@/lib/utils";
import { SceneContainer } from "../shared/SceneContainer";
import { useSceneView } from "../shared/useSceneView";
import { SceneHeader } from "@/components/shared-primitives/SceneHeader";
import { SceneHookHero } from "@/components/shared-primitives/SceneHookHero";
import { AnimatedCounter } from "@/components/shared-primitives/AnimatedCounter";
import { MARKET_RESEARCH } from "../data/marketNumbers";

/* ══════════════════════ Bar descriptor model ══════════════════════ */

type BarTone = "amber" | "red" | "orange" | "emerald";

interface BarSpec {
  /** Stable key for React + a11y IDs. */
  kind: "tam" | "code" | "landing" | "gap";
  /** Uppercase tracked label left of the bar. */
  label: string;
  /** Sub-line in the narrative block (secondary, not on bar). */
  narrativeHeadline: string;
  /** Competitor list or subtext (narrative column only). */
  narrativeBody: string;
  /** Bar value shown at the right — exact copy from wireframe §4.5. */
  value: string;
  /** Final bar width percentage at end of its scroll band. */
  finalWidth: number;
  /** Scroll band [start, end] in scrollYProgress space. */
  scrollBand: [number, number];
  /** Visual treatment. */
  tone: BarTone;
  /** True → narrative stays at opacity 1 + y 0 at end of scroll (don't fade). */
  isLast: boolean;
  /**
   * Honesty contract (S49 — TODO item "Scene 2 waterfall visual clamp tooltip"):
   *
   * `realPercentLabel` is the REAL % (may equal finalWidth for unclamped bars).
   * `clamped` = true iff `finalWidth` ≠ real percent (bar is visually boosted
   * so the sliver doesn't disappear). When true, a <Tooltip> AND a mini-caption
   * below the bar disclose the visual floor explicitly. This is the second
   * layer of defense against the "visual lies" concern: label already says
   * "<2%", but the bar renders at 12% — without disclosure the shape carries
   * more weight than the number. Tooltip + caption together restore honesty.
   */
  realPercentLabel: string;
  clamped: boolean;
  /** Full real value (dollar) repeated in tooltip body. */
  realValueLabel: string;
}

function buildBars(): BarSpec[] {
  return [
    {
      kind: "tam",
      label: "YOUR STACK \u00B7 2026",
      narrativeHeadline: "Every step of building is AI-assisted now.",
      narrativeBody:
        "Cursor writes the code. Lovable ships the app. Framer builds the landing. ChatGPT writes the copy. Solved. Faster than ever.",
      value: "100% \u00B7 every founder today",
      finalWidth: 100,
      scrollBand: [0, 0.25],
      tone: "amber",
      isLast: false,
      realPercentLabel: "100%",
      clamped: false,
      realValueLabel: "every founder with an idea in 2026",
    },
    {
      kind: "code",
      label: "BUILT \u2192 HOPED",
      narrativeHeadline: "~60% build something, then search for buyers.",
      narrativeBody:
        "You shipped with Lovable. Threw up a landing. Tweeted the link. Silence. You never asked who actually has cash ready for this.",
      value: "~60% fail here",
      finalWidth: 60,
      scrollBand: [0.25, 0.5],
      tone: "red",
      isLast: false,
      realPercentLabel: "~60%",
      clamped: false,
      realValueLabel: "tool-first attempts \u2014 no market need",
    },
    {
      kind: "landing",
      label: "COPIED THE TREND",
      narrativeHeadline: "~30% copy what\u2019s trending.",
      narrativeBody:
        "AI-this. Productivity-that. Whatever VCs loved last week. You\u2019re player 47 in a red ocean. The incumbent\u2019s ad budget buries you by Week 2.",
      value: "~30% fail here",
      finalWidth: 30,
      scrollBand: [0.5, 0.75],
      tone: "orange",
      isLast: false,
      realPercentLabel: "~30%",
      clamped: false,
      realValueLabel: "trend-chase attempts \u2014 outcompeted",
    },
    {
      kind: "gap",
      label: "NICHE FIRST \u00B7 THAT\u2019S FOUNDRY",
      narrativeHeadline: "<10% do it backwards. They win.",
      narrativeBody:
        "They find a specific group with cash, pain, and no alternative. THEN they build. First sale in week one. That\u2019s Foundry\u2019s method \u2014 that\u2019s why your next build works.",
      value: "<10% \u00B7 winners",
      finalWidth: 10,
      scrollBand: [0.75, 1.0],
      tone: "emerald",
      isLast: true,
      realPercentLabel: "<10%",
      clamped: false,
      realValueLabel: "niche-first founders",
    },
  ];
}

/* ══════════════════════ Tone tokens ══════════════════════ */

const TONE: Record<
  BarTone,
  {
    bar: string;
    label: string;
    value: string;
    narrativeHeadline: string;
    barShadow?: string;
  }
> = {
  amber: {
    bar: "bg-gradient-to-r from-amber-400/70 to-orange-400/60",
    label: "text-amber-300/70",
    value: "text-white/90",
    narrativeHeadline: "text-amber-200",
  },
  red: {
    bar: "bg-gradient-to-r from-red-500/55 to-rose-500/45",
    label: "text-red-300/70",
    value: "text-white/90",
    narrativeHeadline: "text-red-200",
  },
  orange: {
    bar: "bg-gradient-to-r from-orange-400/60 to-amber-500/50",
    label: "text-orange-300/70",
    value: "text-white/90",
    narrativeHeadline: "text-orange-200",
  },
  emerald: {
    bar: "bg-gradient-to-r from-emerald-400/80 to-teal-400/70",
    label: "text-emerald-300/80",
    value: "text-emerald-300",
    narrativeHeadline: "text-emerald-200",
    barShadow: "shadow-[0_0_30px_-8px_rgba(52,211,153,0.5)]",
  },
};

const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";

function subscribeDesktopViewport(onStoreChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
  mediaQuery.addEventListener("change", onStoreChange);
  return () => mediaQuery.removeEventListener("change", onStoreChange);
}

function getDesktopViewportSnapshot(): boolean {
  if (typeof window === "undefined") return true;
  return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
}

function getDesktopViewportServerSnapshot(): boolean {
  return true;
}

function useDesktopViewport(): boolean {
  return useSyncExternalStore(
    subscribeDesktopViewport,
    getDesktopViewportSnapshot,
    getDesktopViewportServerSnapshot,
  );
}

/* ══════════════════════ Honesty layer — tooltip + visual-floor caption ══════════════════════ */
/**
 * Tooltip library choice: `@base-ui/react/tooltip` (Base UI by Floating UI / ex-Radix
 * team). Rationale:
 *   - Project already depends on `@base-ui/react` (see package.json) for Button +
 *     Input primitives, so no new dependency is introduced (library-first rule).
 *   - Adding `@radix-ui/react-tooltip` separately would mean two floating-UI stacks
 *     bundled together (Base UI is the Radix team's newer rewrite atop Floating UI) —
 *     ~25kb of duplicated positioning math.
 *   - Base UI's Tooltip has the same a11y posture as Radix: keyboard-focus hover
 *     parity, `role="tooltip"` on popup, `aria-describedby` auto-wired from
 *     trigger → popup via internal IDs, ESC to dismiss, Portal to escape scroll
 *     container / overflow clips.
 *
 * Why not native <title> in SVG / HTML? The bars are <div> + gradient, not SVG,
 * so `<title>` would render as a browser's slow-tooltip — no stylistic control,
 * no keyboard-focus disclosure, no tap-to-show on mobile, no reduced-motion
 * compliance (browser native tooltips have no motion anyway, but their styling
 * looks out-of-place against the cinematic scene). Base UI tooltip gives us
 * design-system colors + rounded-lg + subtle ring + portal-escape.
 *
 * Mobile tap-to-show: Base UI's default hover/focus interaction pattern treats
 * touchstart as focus-equivalent for non-button triggers when `closeOnClick=false`.
 * We do NOT set closeOnClick=false because we want tap → show → tap-again → hide.
 * The trigger is a <div tabIndex={0}>, so focus lands on tap on iOS/Android.
 *
 * Reduced-motion: Base UI tooltip uses CSS transitions which respect
 * `prefers-reduced-motion: reduce` in modern browsers (transition-duration
 * inherits from system). We additionally drop any bounce easings — our popup
 * uses opacity-only CSS transitions, no transform bounce.
 */

/**
 * Wraps a bar "track" (the 100%-wide rounded container) with a tooltip trigger
 * + popup disclosing the real % and value. For clamped bars the popup has a
 * second line explaining the visual floor. Renders as a <div> (not <button>),
 * so it doesn't interfere with the scene's non-interactive contract beyond
 * adding a keyboard focus stop for accessibility.
 */
function BarTooltip({
  bar,
  children,
  describeId,
}: {
  bar: BarSpec;
  children: ReactNode;
  /** Element id rendered in the trigger via `aria-describedby`. */
  describeId: string;
}) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger
        render={
          <div
            tabIndex={0}
            role="group"
            aria-describedby={describeId}
            aria-label={
              bar.clamped
                ? `${bar.label}: real ${bar.realPercentLabel} · ${bar.realValueLabel}. Bar visually clamped to ${bar.finalWidth}% for legibility.`
                : `${bar.label}: ${bar.realPercentLabel} · ${bar.realValueLabel}.`
            }
          />
        }
      >
        {children}
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Positioner side="top" align="center" sideOffset={8}>
          <Tooltip.Popup
            id={describeId}
            className="z-50 max-w-[260px] rounded-lg border border-white/10 bg-neutral-900/95 px-3 py-2 text-left font-sans text-xs leading-relaxed text-white/90 shadow-xl ring-1 ring-inset ring-white/5 backdrop-blur-sm transition-[opacity] duration-150 data-[starting-style]:opacity-0 data-[ending-style]:opacity-0"
          >
            <p className="font-heading text-[10px] font-semibold uppercase tracking-[0.16em] text-white/60">
              {bar.label}
            </p>
            <p className="mt-1 font-mono text-[13px] tabular-nums text-white">
              {bar.realPercentLabel} · {bar.realValueLabel}
            </p>
            {bar.clamped && (
              <p className="mt-2 text-[11px] text-emerald-200/80">
                Visual minimum {bar.finalWidth}% for legibility —
                actual {bar.realPercentLabel}.
              </p>
            )}
          </Tooltip.Popup>
        </Tooltip.Positioner>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

/**
 * Mini-caption rendered BELOW a clamped bar. Second layer of honesty defense
 * (tooltip is the first, but requires a hover/focus — users who only scan the
 * visuals should still see the disclaimer). Tiny monospace, low contrast so
 * it doesn't compete with the main bar value on the right.
 */
function VisualFloorCaption({ bar }: { bar: BarSpec }) {
  if (!bar.clamped) return null;
  return (
    <p
      aria-hidden
      className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-emerald-200/55"
    >
      {"\u25BC"} visual floor · real {bar.realPercentLabel}
    </p>
  );
}

/* ══════════════════════ SR-only figure caption ══════════════════════ */

function buildSrCaption(): string {
  return (
    "Founder attempt waterfall. " +
    "100 percent of founders in 2026 have AI tools for every step of building. " +
    "Approximately 60 percent fail by building first, then searching for buyers. " +
    "Approximately 30 percent fail by copying a trending idea and losing in a red ocean. " +
    "Fewer than 10 percent do it backwards — find an underserved niche first, then build — and win. " +
    "That is Foundry's method."
  );
}

/* ══════════════════════════════════════════════════════════════════
 * Scene 2 — root
 * ══════════════════════════════════════════════════════════════════ */

export default function Scene2TheGap() {
  const reduced = useReducedMotion();
  const isDesktopViewport = useDesktopViewport();
  const bars = buildBars();
  const srCaption = buildSrCaption();

  useSceneView({
    sceneId: "2",
    sceneName: "The Gap",
    elementId: "scene-2-gap",
  });

  return (
    <SceneContainer
      id="scene-2-gap"
      ariaLabel="The Gap — AI tooling market waterfall"
      // Desktop: 200vh scroll runway for the sticky pin. Mobile owns its own
      // compact sticky runway in MobileWaterfall.
      // Reduced-motion: override to a shorter static layout via flex inside.
      minHeight={reduced ? "min-h-screen" : "min-h-[140vh]"}
    >
      {/*
       * Tooltip.Provider groups all 4 bar tooltips so once the first one opens
       * subsequent ones appear instantly (no re-delay when sweeping across
       * bars with keyboard or pointer). 600ms default delay is kept so casual
       * scroll past doesn't flash tooltips.
       */}
      <Tooltip.Provider delay={600} closeDelay={100}>
        <div className="mx-auto max-w-6xl px-4 md:px-6">
        {/*
         * ─── Scene header + hook (above pin zone) ───
         * Vertical rhythm note: hero padding kept intentionally **tight**
         * (pt-12 md:pt-16 — was pt-16 md:pt-24) so that the 200vh sticky
         * runway below does not create a perceived empty canvas between
         * hook text and the moment `sticky top-0` pins the chart. Users
         * scrolling past the hook should see the waterfall chart engage
         * almost immediately, not land on blank space.
         */}
        <div className="pt-8 md:pt-10">
          <SceneHeader id="scene-2-gap-title" number={2} title="The Gap" />

          <SceneHookHero gradient stagger>
            AI solved every step of shipping. Except the one that kept killing
            your past attempts.
          </SceneHookHero>

          <p className="mt-4 max-w-2xl font-sans text-xs italic text-white/40 md:text-sm">
            Pattern estimates from CB Insights &ldquo;Why Startups Fail&rdquo; (42% no market need, 19% outcompeted), YC post-mortems, and indie-hacker teardowns.
          </p>
        </div>

        {/* ─── Sticky scroll-pin waterfall (desktop only) ─── */}
        {/* Wrapped in a dedicated container so useScroll can track its range. */}
        {reduced ? (
          <StaticWaterfall bars={bars} srCaption={srCaption} />
        ) : isDesktopViewport ? (
          <DesktopWaterfall bars={bars} srCaption={srCaption} />
        ) : (
          <MobileWaterfall bars={bars} srCaption={srCaption} />
        )}

          {/* ─── Why Now callout (below pin zone) ─── */}
          <WhyNowCallout reduced={!!reduced} />
        </div>
      </Tooltip.Provider>
    </SceneContainer>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * Desktop variant — CSS sticky (lg and up)
 * ══════════════════════════════════════════════════════════════════ */

/**
 * Pick the active narrative index from a scrollYProgress value.
 *
 * Why a state-driven active index instead of per-block useTransform opacity?
 *   The prior implementation stacked 4 <DesktopNarrativeBlock>s absolutely,
 *   each subscribing to the same `scrollYProgress` MotionValue via a 4-keyframe
 *   useTransform envelope ([start, fadeInEnd, holdEnd, fadeOutEnd] →
 *   [0, 1, 1, 0]). Observed in production: narrative caption froze on bar #2
 *   (CODE BUILDERS) across progress 0.25 → 1.0 — bars animated correctly but
 *   the narrative layer stopped switching. Root cause was framer-motion v12
 *   subscription staleness: `buildBars()` returns a fresh array on every
 *   render, so the inline `[start, fadeInEnd, holdEnd, fadeOutEnd]` arrays
 *   passed to useTransform were new-identity each render, triggering internal
 *   mapper reset races when 4 sibling subscribers share one source MV.
 *
 *   Switching to a single `useMotionValueEvent("change")` subscription + a
 *   `useState` active index → single source of truth, no per-block MV races,
 *   and only one narrative block mounted at a time via <AnimatePresence>.
 *   This also gives crisp fade-in/out between bands (was ramp+hold+ramp),
 *   matching the "SS4 → SS5 → SS6 should crossfade" UX spec.
 *
 * Bands match `bar.scrollBand`: AI [0, 0.25] · CODE [0.25, 0.5] · LANDING
 * [0.5, 0.75] · GAP [0.75, 1.0]. Half-open at the lower bound, inclusive at
 * the upper — progress=1.0 stays on GAP (isLast).
 */
function activeBarIndex(progress: number): number {
  if (progress >= 0.75) return 3;
  if (progress >= 0.5) return 2;
  if (progress >= 0.25) return 1;
  return 0;
}

function DesktopWaterfall({
  bars,
  srCaption,
}: {
  bars: BarSpec[];
  srCaption: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Offset tuned to the pin window:
  //   start 10% → progress=0 when container top hits the `sticky top-[10vh]`
  //                pin line (chart visually "locks" at mid-screen).
  //   end 90%  → progress=1 when container bottom hits viewport's 90% line
  //               = sticky child bottom (pin disengage).
  // Maps the 0→1 progress band exactly onto the 20vh pin runway, so bars
  // fill across the full time the chart is pinned — no dead scroll before
  // pin engage, no dead scroll after animation completes.
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 15%", "end 80%"],
  });

  // Single subscriber → single source of truth for which narrative is visible.
  // See `activeBarIndex` doc for why this replaces per-block useTransform.
  const [activeIdx, setActiveIdx] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const next = activeBarIndex(v);
    setActiveIdx((prev) => (prev === next ? prev : next));
  });
  const activeBar = bars[activeIdx] ?? bars[0];

  return (
    <div
      ref={containerRef}
      // Desktop runway — sticky child pins for 65vh mid-viewport
      // (top-[15vh] ... h-[65vh]). Container = sticky_h + 25vh runway =
      // 90vh total. Sticky height shrunk to ~match chart card content
      // (header + 4 bars + counter ≈ 62vh) — earlier 80vh left a tall
      // band of empty inside the pinned zone below the bars, which read
      // as a hole between the chart and the Why Now callout.
      className="relative mt-4 hidden lg:block"
      style={{ height: "90vh" }}
    >
      {/*
       * Sticky child centred vertically (top-[15vh] with h-[65vh]) so the
       * chart pins MID-VIEWPORT. Height tuned to actual chart card content
       * so no dead band appears below the bars during pin — previously a
       * 80vh sticky wrapped ~50vh of content, leaving ~30vh of empty pinned
       * space that read as a hole between chart end and Why Now below.
       */}
      <div className="sticky top-[15vh] grid h-[65vh] grid-cols-5 items-start gap-8 pt-6 pb-4">
        {/* ─── Left column (40%): active narrative block (crossfade) ─── */}
        {/*
         * `mode="popLayout"` (default for AnimatePresence with absolute children):
         * the outgoing block exits while the incoming block enters in the same
         * absolute slot — clean crossfade, no layout thrash, no wait-for-exit
         * lag during fast scroll (which `mode="wait"` would introduce).
         */}
        <div className="relative col-span-2 h-full">
          <AnimatePresence initial={false}>
            <DesktopNarrativeBlock key={activeBar.kind} bar={activeBar} />
          </AnimatePresence>
        </div>

        {/* ─── Right column (60%): pinned chart ─── */}
        <figure
          role="img"
          aria-labelledby="waterfall-caption-desktop"
          className="col-span-3 overflow-hidden rounded-2xl border border-white/10 bg-surface-card p-8 ring-1 ring-inset ring-white/5"
        >
          <figcaption
            id="waterfall-caption-desktop"
            className="sr-only"
          >
            {srCaption}
          </figcaption>

          <p
            aria-hidden
            className="mb-8 font-heading text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-300/70"
          >
            Founder attempt waterfall · 2026
          </p>

          <div className="space-y-7">
            {bars.map((bar) => (
              <DesktopBarRow
                key={bar.kind}
                bar={bar}
                scrollYProgress={scrollYProgress}
              />
            ))}
          </div>
        </figure>
      </div>
    </div>
  );
}

/* ══════════════════════ Desktop bar row ══════════════════════ */

function DesktopBarRow({
  bar,
  scrollYProgress,
}: {
  bar: BarSpec;
  scrollYProgress: MotionValue<number>;
}) {
  const tone = TONE[bar.tone];
  const [start, end] = bar.scrollBand;

  const widthMV = useTransform(
    scrollYProgress,
    [start, end],
    [0, bar.finalWidth],
    { clamp: true },
  );
  const widthCSS = useTransform(widthMV, (v) => `${v}%`);

  // Gap bar gets a counter that ticks in as scroll enters its band.
  const showCounter = bar.kind === "gap";
  const tipId = `scene-2-bar-tip-desktop-${bar.kind}`;

  return (
    <div className="space-y-2">
      {/* Row: label left · track middle · value right */}
      <div className="flex items-baseline justify-between gap-4">
        <span
          className={cn(
            "font-heading text-[11px] font-semibold uppercase tracking-[0.18em]",
            tone.label,
          )}
        >
          {bar.label}
          {bar.kind === "gap" && (
            <span className="ml-2 font-sans text-[11px] not-italic tracking-normal text-emerald-200/70">
              ← Foundry lives here
            </span>
          )}
        </span>
        <span
          className={cn(
            "font-mono text-sm font-semibold tabular-nums",
            tone.value,
          )}
        >
          <SyncedBarValue bar={bar} progressValue={widthMV} />
        </span>
      </div>

      <BarTooltip bar={bar} describeId={tipId}>
        <div className="relative h-10 overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.04] outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/60 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent">
          <motion.div
            style={{ width: widthCSS }}
            className={cn(
              "absolute inset-y-0 left-0 rounded-xl will-change-[width]",
              tone.bar,
              tone.barShadow,
            )}
          />
        </div>
      </BarTooltip>

      <VisualFloorCaption bar={bar} />

      {showCounter && (
        <div className="flex justify-end pt-1">
          <span className="font-heading text-2xl font-bold tabular-nums text-emerald-300">
            <AnimatedCounter
              from={0}
              to={10}
              formatter={formatPercent}
              prefix="<"
              duration={1.2}
            />
          </span>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════ Desktop narrative block (active-index driven) ══════════════════════ */

/**
 * Single active narrative block, rendered inside <AnimatePresence> in
 * DesktopWaterfall. Enter/exit handled by framer-motion lifecycle (no
 * scrollYProgress subscription here) — the parent decides which block to mount
 * based on scroll band. Crossfade duration kept short (250ms) so it feels
 * punchy against the waterfall bar fill.
 */
function DesktopNarrativeBlock({ bar }: { bar: BarSpec }) {
  const tone = TONE[bar.tone];

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      // Absolute-positioned so successive blocks crossfade in the same slot
      // without layout thrash. `pointer-events-none` defends against any
      // stray focus/hover during exit; tooltips live on bars (right column).
      //
      // `top-8` (was `top-1/2 -translate-y-1/2`): the chart moved to
      // `items-start` on the sticky grid, so vertically-centered narrative
      // no longer lines up. `top-8` matches the chart card's inner `p-8`
      // so the narrative label sits level with the chart's top edge.
      className="pointer-events-none absolute inset-x-0 top-8"
    >
      <p
        className={cn(
          "mb-3 font-heading text-[11px] font-semibold uppercase tracking-[0.2em]",
          tone.label,
        )}
      >
        {bar.label}
      </p>

      <p
        className={cn(
          "font-heading text-2xl font-bold leading-tight lg:text-3xl",
          tone.narrativeHeadline,
        )}
      >
        {bar.narrativeHeadline}
      </p>

      <p className="mt-4 font-sans text-base leading-relaxed text-white/80 lg:text-[17px]">
        {bar.narrativeBody}
      </p>
    </motion.article>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * Mobile variant — sticky chart + active narrative (< lg)
 * ══════════════════════════════════════════════════════════════════ */

function MobileWaterfall({
  bars,
  srCaption,
}: {
  bars: BarSpec[];
  srCaption: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 20%", "end 88%"],
  });

  const [activeIdx, setActiveIdx] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const next = activeBarIndex(v);
    setActiveIdx((prev) => (prev === next ? prev : next));
  });
  const activeBar = bars[activeIdx] ?? bars[0];

  return (
    <div
      ref={containerRef}
      // Mobile-only runway. The sticky stack stays below the navbar and owns
      // both chart + narrative, so copy changes with the bars without sliding
      // behind the chart or navbar.
      className="relative mt-5 lg:hidden"
      style={{ height: "245vh" }}
    >
      <div className="sticky top-[76px] z-10">
        <figure
          role="img"
          aria-labelledby="waterfall-caption-mobile"
          className="overflow-hidden rounded-2xl border border-white/10 bg-surface-card/95 p-4 ring-1 ring-inset ring-white/5 backdrop-blur-sm"
        >
          <figcaption id="waterfall-caption-mobile" className="sr-only">
            {srCaption}
          </figcaption>
          <p
            aria-hidden
            className="mb-4 font-heading text-[9px] font-semibold uppercase tracking-[0.2em] text-amber-300/70"
          >
            Founder attempt waterfall · 2026
          </p>
          <div className="space-y-5">
            {bars.map((bar) => (
              <MobileBarRow
                key={bar.kind}
                bar={bar}
                active={bar.kind === activeBar.kind}
                scrollYProgress={scrollYProgress}
              />
            ))}
          </div>
        </figure>

        <div className="mt-6 px-1">
          <AnimatePresence mode="wait" initial={false}>
            <MobileActiveNarrative key={activeBar.kind} bar={activeBar} />
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════ Mobile bar row (compact pinned) ══════════════════════ */

function MobileBarRow({
  bar,
  active,
  scrollYProgress,
}: {
  bar: BarSpec;
  active: boolean;
  scrollYProgress: MotionValue<number>;
}) {
  const tone = TONE[bar.tone];
  const [start, end] = bar.scrollBand;

  const widthMV = useTransform(
    scrollYProgress,
    [start, end],
    [0, bar.finalWidth],
    { clamp: true },
  );
  const widthCSS = useTransform(widthMV, (v) => `${v}%`);
  const tipId = `scene-2-bar-tip-mobile-${bar.kind}`;

  return (
    <div
      className={cn(
        "-mx-2 space-y-1.5 rounded-xl border px-2 py-2 transition-colors duration-200",
        active
          ? "border-white/12 bg-white/[0.055] shadow-[0_0_22px_-18px_rgba(251,191,36,0.9)]"
          : "border-transparent bg-transparent",
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <span
          className={cn(
            "max-w-[48%] font-heading text-[9px] font-semibold uppercase tracking-[0.14em]",
            tone.label,
          )}
        >
          {bar.label}
        </span>
        <span
          className={cn(
            "max-w-[48%] text-right font-mono text-[10px] font-semibold tabular-nums",
            tone.value,
          )}
        >
          <SyncedBarValue bar={bar} progressValue={widthMV} />
        </span>
      </div>
      <BarTooltip bar={bar} describeId={tipId}>
        <div className="relative h-3 w-full overflow-hidden rounded-full border border-white/[0.08] bg-white/[0.04] outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/60">
          <motion.div
            style={{ width: widthCSS }}
            className={cn(
              "absolute inset-y-0 left-0 rounded-full will-change-[width]",
              tone.bar,
              tone.barShadow,
            )}
          />
        </div>
      </BarTooltip>
      {bar.clamped && (
        <p
          aria-hidden
          className="font-mono text-[9px] uppercase tracking-[0.14em] text-emerald-200/55"
        >
          {"\u25BC"} visual floor · real {bar.realPercentLabel}
        </p>
      )}
    </div>
  );
}

function SyncedBarValue({
  bar,
  progressValue,
}: {
  bar: BarSpec;
  progressValue: MotionValue<number>;
}) {
  const reduced = useReducedMotion();
  const [displayValue, setDisplayValue] = useState(() =>
    reduced ? bar.finalWidth : progressValue.get(),
  );

  useMotionValueEvent(progressValue, "change", (value) => {
    setDisplayValue(reduced ? bar.finalWidth : value);
  });

  return (
    <span aria-label={bar.value}>
      {formatSyncedBarValue(bar, displayValue)}
    </span>
  );
}

function formatSyncedBarValue(bar: BarSpec, value: number): string {
  const rounded = Number.isFinite(value)
    ? Math.max(0, Math.round(value))
    : 0;

  switch (bar.kind) {
    case "tam":
      return rounded > 0 ? `${rounded}% \u00B7 every founder today` : "0%";
    case "code":
      return rounded > 0 ? `~${rounded}% fail here` : "0%";
    case "landing":
      return rounded > 0 ? `~${rounded}% fail here` : "0%";
    case "gap":
      return rounded > 0
        ? `<${Math.max(1, rounded)}% \u00B7 winners`
        : "0%";
  }
}

/* ══════════════════════ Mobile active narrative ══════════════════════ */

function MobileActiveNarrative({ bar }: { bar: BarSpec }) {
  const tone = TONE[bar.tone];

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="min-h-[172px]"
    >
      <p
        className={cn(
          "mb-2 font-heading text-[10px] font-semibold uppercase tracking-[0.2em]",
          tone.label,
        )}
      >
        {bar.label}
      </p>
      <p
        className={cn(
          "font-heading text-xl font-bold leading-tight",
          tone.narrativeHeadline,
        )}
      >
        <MobileNarrativeHeadline bar={bar} />
      </p>
      <p className="mt-3 font-sans text-[15px] leading-relaxed text-white/80">
        {bar.narrativeBody}
      </p>
    </motion.article>
  );
}

function MobileNarrativeHeadline({ bar }: { bar: BarSpec }) {
  switch (bar.kind) {
    case "code":
      return (
        <>
          <AnimatedCounter
            from={0}
            to={60}
            formatter={formatPercent}
            prefix="~"
            duration={1.1}
            startOnView={false}
          />{" "}
          build something, then search for buyers.
        </>
      );
    case "landing":
      return (
        <>
          <AnimatedCounter
            from={0}
            to={30}
            formatter={formatPercent}
            prefix="~"
            duration={1.1}
            startOnView={false}
          />{" "}
          copy what&apos;s trending.
        </>
      );
    case "gap":
      return (
        <>
          <AnimatedCounter
            from={0}
            to={10}
            formatter={formatPercent}
            prefix="<"
            duration={1.1}
            startOnView={false}
          />{" "}
          do it backwards. They win.
        </>
      );
    case "tam":
      return <>{bar.narrativeHeadline}</>;
  }
}

/* ══════════════════════════════════════════════════════════════════
 * Reduced-motion / no-JS variant — static stacked
 * ══════════════════════════════════════════════════════════════════ */

function StaticWaterfall({
  bars,
  srCaption,
}: {
  bars: BarSpec[];
  srCaption: string;
}) {
  return (
    <div className="mt-10 space-y-8">
      <figure
        role="img"
        aria-labelledby="waterfall-caption-static"
        className="overflow-hidden rounded-2xl border border-white/10 bg-surface-card p-6 ring-1 ring-inset ring-white/5"
      >
        <figcaption id="waterfall-caption-static" className="sr-only">
          {srCaption}
        </figcaption>
        <p
          aria-hidden
          className="mb-5 font-heading text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-300/70"
        >
          Founder attempt waterfall · 2026
        </p>
        <div className="space-y-5">
          {bars.map((bar) => (
            <StaticBarRow key={bar.kind} bar={bar} />
          ))}
        </div>
      </figure>

      <div className="space-y-6">
        {bars.map((bar) => {
          const tone = TONE[bar.tone];
          return (
            <article key={bar.kind}>
              <p
                className={cn(
                  "mb-2 font-heading text-[10px] font-semibold uppercase tracking-[0.2em]",
                  tone.label,
                )}
              >
                {bar.label}
              </p>
              <p
                className={cn(
                  "font-heading text-xl font-bold leading-tight md:text-2xl",
                  tone.narrativeHeadline,
                )}
              >
                {bar.narrativeHeadline}
              </p>
              <p className="mt-2 font-sans text-base leading-relaxed text-white/80">
                {bar.narrativeBody}
              </p>
            </article>
          );
        })}
      </div>
    </div>
  );
}

function StaticBarRow({ bar }: { bar: BarSpec }) {
  const tone = TONE[bar.tone];
  const tipId = `scene-2-bar-tip-static-${bar.kind}`;
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <span
          className={cn(
            "font-heading text-[10px] font-semibold uppercase tracking-[0.18em]",
            tone.label,
          )}
        >
          {bar.label}
        </span>
        <span
          className={cn(
            "font-mono text-xs font-semibold tabular-nums",
            tone.value,
          )}
        >
          {bar.value}
        </span>
      </div>
      <BarTooltip bar={bar} describeId={tipId}>
        <div className="relative h-7 overflow-hidden rounded-lg border border-white/[0.08] bg-white/[0.04] outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/60">
          <div
            className={cn(
              "absolute inset-y-0 left-0 rounded-lg",
              tone.bar,
              tone.barShadow,
            )}
            style={{ width: `${bar.finalWidth}%` }}
          />
        </div>
      </BarTooltip>
      <VisualFloorCaption bar={bar} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * Why Now callout (below pin)
 * ══════════════════════════════════════════════════════════════════ */

function WhyNowCallout({ reduced }: { reduced: boolean }) {
  const { whyNow } = MARKET_RESEARCH;

  return (
    <div className="mt-6 md:mt-10">
      <div className="mb-4 flex items-center gap-2 text-amber-300/70">
        <span aria-hidden className="font-sans text-[14px] leading-none">↓</span>
        <p className="font-heading text-[11px] font-semibold uppercase tracking-[0.2em]">
          Why Now
        </p>
      </div>
      <motion.blockquote
        initial={reduced ? false : { opacity: 0, y: 16 }}
        whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: reduced ? 0 : 0.4, ease: "easeOut" }}
        className="max-w-3xl rounded-xl border-l-[3px] border-amber-400/60 bg-amber-400/[0.06] px-6 py-5"
      >
        <p className="font-heading text-[22px] font-bold italic leading-[1.5] text-white/80 md:text-2xl">
          {whyNow.line1}
        </p>
        <p className="mt-2 font-heading text-[22px] font-bold italic leading-[1.5] text-white/80 md:text-2xl">
          {whyNow.line2}
        </p>
        <p className="mt-3 font-heading text-[22px] font-bold italic leading-[1.5] text-emerald-200 md:text-2xl">
          {whyNow.line3}
        </p>
      </motion.blockquote>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * Helpers
 * ══════════════════════════════════════════════════════════════════ */

/**
 * Compact USD — "$1.0B", "$750M", "$30M", "$2M". Keeps the bar values /
 * counter output in a tight tabular-nums column.
 */
/**
 * Percent formatter — "10%" / "0%". Used by the emerald-bar AnimatedCounter to
 * reveal the "winners" share at end of the waterfall scroll.
 */
function formatPercent(n: number): string {
  return `${Math.max(0, Math.round(n))}%`;
}
